import React, { FunctionComponent } from 'react'
import { FormattedMessage } from 'react-intl'
import { ImageSourcePropType, View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { PRODUCT_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/productType'

import wellnessCheckInResultsGetStartedImage from '../../assets/WellnessCheckInProviders.png'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { EssentialsOnboardingIcon } from '../../atoms/illustrations/EssentialsOnboardingIcon'
import { Image } from '../../atoms/image/Image'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { Title } from '../../atoms/title/Title'
import { useMediaQuerySize } from '../../hooks/useMediaQuerySize'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface WellnessCheckInResultsSubFooterProps {
  onCTAPress: () => void
}

const Container = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundSecondary,
  padding: `${theme.spacing['64px']} 0`,
  alignItems: 'center',
}))

const TextContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  padding: `0 ${theme.spacing['16px']}`,
  margin: 'auto',
}))

const TitleTextContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['24px'],
  marginBottom: theme.spacing['8px'],
}))

const DescriptionTextContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['24px'],
}))

const ImageContainer = styled(Image)<{ theme: ThemeType }>(({ theme }) => ({
  width: theme.breakpoints.isMobileSized ? '172px' : '224px',
  height: theme.breakpoints.isMobileSized ? '128px' : '166px',
  margin: 'auto',
}))

const ButtonContainer = styled.View<{ theme: ThemeType }>(({}) => ({
  margin: 'auto',
}))

export const WellnessCheckInResultsSubFooter: FunctionComponent<WellnessCheckInResultsSubFooterProps> = ({
  onCTAPress,
}) => {
  const { colors, isLyraSelect } = useTheme() as ThemeType
  const { isMobileSized } = useMediaQuerySize()

  const contentByProductType = {
    [PRODUCT_TYPE.WFMH]: {
      image: (
        <ImageContainer
          source={wellnessCheckInResultsGetStartedImage as ImageSourcePropType}
          contentFit='cover'
          accessibilityIgnoresInvertColors
        />
      ),
      title: (
        <FormattedMessage
          defaultMessage='Interested in talking with someone about your wellbeing?'
          description='Title for section that leads user to search for care'
        />
      ),
      description: (
        <FormattedMessage
          defaultMessage='Discover personalized care recommendations and match with the right provider.'
          description='Subtitle for section that leads user to search for care'
        />
      ),
      buttonText: (
        <FormattedMessage
          defaultMessage='Get started'
          description='Text for button that leads user to search for care'
        />
      ),
    },
    [PRODUCT_TYPE.SELECT]: {
      image: <EssentialsOnboardingIcon />,
      title: (
        <FormattedMessage
          defaultMessage='Discover more resources'
          description='Title for section that leads user to library of self-care resources'
        />
      ),
      description: (
        <FormattedMessage
          defaultMessage='Find meditations, videos, articles, courses, and more'
          description='Subtitle for section that leads user to library of self-care resources'
        />
      ),
      buttonText: (
        <FormattedMessage
          defaultMessage='Explore the library'
          description='Text for button that leads user to library of self-care resources'
        />
      ),
    },
  }

  const content = contentByProductType[isLyraSelect ? PRODUCT_TYPE.SELECT : PRODUCT_TYPE.WFMH]

  return (
    <View testID={tID(`WellnessCheckInResultsSubFooter`)}>
      <Container>
        {content.image}
        <TextContainer>
          <TitleTextContainer>
            <Title
              level='2'
              size={isMobileSized ? TitleSize.MEDIUM : TitleSize.LARGE}
              text={content.title}
              textAlign='center'
            />
          </TitleTextContainer>
          <DescriptionTextContainer>
            <BodyText
              text={content.description}
              size={BodyTextSize.DEFAULT}
              color={colors.textSecondary}
              textAlign='center'
            />
          </DescriptionTextContainer>
          <ButtonContainer>
            <PrimaryButton
              onPress={onCTAPress}
              text={content.buttonText}
              testID={tID('WellnessCheckInResultsSubFooterButton')}
            />
          </ButtonContainer>
        </TextContainer>
      </Container>
    </View>
  )
}
